import type { Suggestion } from '@/lib/similarFeedback';
import { snippet, wantsLabel } from '@/lib/everyonesFeedback';
import { CATEGORY_LABEL, statusLabel } from '@/lib/feedbackMine';
import MeTooControl from './MeTooControl';
import type { MeTooHandlers } from './EveryonesFeedback';

/** Its own `fbs-` styles, declared here: a class in this app is live only while the component that declares it is mounted. */
const css = `
  .fbs { display: flex; flex-direction: column; gap: 8px; padding: 10px 12px; border: 1px solid var(--ew-warning); border-radius: var(--ew-r-md); background: var(--ew-n-50); }
  .fbs-title { margin: 0; font: var(--ew-fw-semibold) 12.5px/1.2 var(--ew-font); color: var(--ew-dark); }
  .fbs-sub { margin: 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .fbs-item { display: flex; flex-direction: column; gap: 6px; padding: 8px 10px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-sm); background: #fff; }
  .fbs-meta { display: flex; align-items: center; gap: 8px; font: var(--ew-fw-medium) 11px/1 var(--ew-font); color: var(--ew-n-500); }
  .fbs-wants { color: var(--ew-dark); font-weight: var(--ew-fw-semibold); }
  .fbs-first { margin: 0; overflow-wrap: anywhere; font: var(--ew-fw-regular) 13px/1.4 var(--ew-font); color: var(--ew-dark); }
  .fbs-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .fbs-no:disabled { opacity: 0.5; cursor: default; }
  .fbs-no { background: transparent; border: 0; padding: 0; cursor: pointer; color: var(--ew-n-500); text-decoration: underline; font: var(--ew-fw-regular) 12px/1.2 var(--ew-font); }
  .fbs-kept { margin: 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
`;

interface Props {
  suggestions: readonly Suggestion[];
  meToo: MeTooHandlers;
  /** "Not the same": this item is not shown again for this message. */
  onNotTheSame: (id: number) => void;
}

/**
 * "Similar requests", under the box while somebody types: what is already on the board that reads like what they are writing, each
 * with **Me too** and **Not the same**. Saying "me too" adds their voice instead of a second copy; their message stays in the box, in
 * case they still want to send it as well.
 *
 * Shows nothing at all when there is nothing to suggest: no heading over an empty panel.
 */
export default function SimilarRequests({ suggestions, meToo, onNotTheSame }: Props) {
  if (suggestions.length === 0) return null;
  const added = suggestions.some((s) => meToo.justAdded.has(s.item.id));
  return (
    <section className="fbs" aria-label="Similar requests">
      <style>{css}</style>
      <h3 className="fbs-title">Similar requests</h3>
      <p className="fbs-sub">Is yours one of these? Say “me too” to add your voice instead of sending it again.</p>
      {suggestions.map(({ item: i }) => {
        const label = statusLabel(i.status, i.closedReason);
        const wants = wantsLabel(i.followerCount);
        return (
          <div key={i.id} className="fbs-item">
            <span className="fbs-meta">
              <span>{CATEGORY_LABEL[i.category] ?? i.category}</span>
              <span>{label}</span>
              {wants && <span className="fbs-wants">{wants}</span>}
            </span>
            <p className="fbs-first">{snippet(i.message, 160)}</p>
            <div className="fbs-actions">
              <MeTooControl
                item={i}
                about={snippet(i.message, 50)}
                justAdded={meToo.justAdded.has(i.id)}
                pending={meToo.pending.has(i.id)}
                error={meToo.errors.get(i.id) ?? null}
                onMeToo={meToo.onMeToo}
                onUndo={meToo.onUndo}
              />
              {/* "Not the same" is for any suggestion that is not what is being written, whether it is open, closed, mine or one I follow: otherwise
                  an item that cannot be acted on would sit under the box, and hold one of its three places, for as long as the text matches. The one
                  just added to keeps its "Added" until the message is sent or the drawer closed. ⚠️ OFF WHILE "ME TOO" IS ON ITS WAY for it: a dismissal
                  then would take the suggestion, and the "Added" and the Undo that come with it, off the screen while the follow goes ahead. (Codex, #695) */}
              {!meToo.justAdded.has(i.id) && (
                <button
                  type="button" className="ew-btn-link fbs-no" disabled={meToo.pending.has(i.id)}
                  aria-label={`Not the same: ${snippet(i.message, 50)}`} onClick={() => onNotTheSame(i.id)}
                >
                  Not the same
                </button>
              )}
            </div>
          </div>
        );
      })}
      {added && <p className="fbs-kept">Your message is still here, if you want to send it as well.</p>}
    </section>
  );
}
